import Cookies from 'js-cookie';

const isLocalFrontendHost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost'
    || window.location.hostname === '127.0.0.1'
);

const apiUrl = isLocalFrontendHost
    ? (process.env.REACT_APP_LOCAL_API_URL || '')
    : (process.env.REACT_APP_API_URL || '');

const getCsrfToken = () => {
    return Cookies.get('csrf_token')
        || Cookies.get('csrftoken')
        || Cookies.get('csrf')
        || Cookies.get('XSRF-TOKEN')
        || '';
};

const refreshCsrfToken = async () => {
    try {
        await fetch(`${apiUrl}/api/auth/`, {
            method: 'GET',
            credentials: 'include',
        });
    } catch (error) {
        return '';
    }

    return getCsrfToken();
};

const extractFailureData = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
    if ('data' in value) return value.data;

    const entries = Object.entries(value).filter(([key]) => key !== 'code');
    if (entries.length === 0) return null;

    return Object.fromEntries(entries);
};

const hasCsrfError = (value) => {
    if (!value) return false;
    if (typeof value === 'string') return /csrf/i.test(value);
    if (Array.isArray(value)) return value.some((entry) => typeof entry === 'string' && /csrf/i.test(entry));
    if (typeof value !== 'object') return false;

    return Object.entries(value).some(([key, entry]) => {
        if (/csrf/i.test(key)) return true;
        if (Array.isArray(entry)) return entry.some((item) => typeof item === 'string' && /csrf/i.test(item));
        return typeof entry === 'string' && /csrf/i.test(entry);
    });
};

const normalizeSuccessData = (value) => {
    if (value && typeof value === 'object') {
        if ('data' in value) return value.data;
        if ('user' in value) return value.user;
        if ('session' in value) return value.session;
        if ('account' in value) return value.account;
    }

    return null;
};

const normalizeApiErrors = (value, fallbackMessage) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) return value;
    if (Array.isArray(value)) return { msg: value };
    if (typeof value === 'string' && value.trim().length > 0) return { msg: [value] };
    return { msg: [fallbackMessage] };
};

export const rand = (min, max) => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
};

// Allow: letters, numbers, @ and .
const LOGIN_CHARACTERS = /^[A-Za-z0-9@.]+$/;
export const isValidLogin = (s) => LOGIN_CHARACTERS.test(s);

// Allow: letters, numbers, @ , . ! # $ % ^ & *
const PASSWORD_CHARACTERS = /^[A-Za-z0-9@,.!#$%^&*]+$/;
export const isValidPassword = (s) => PASSWORD_CHARACTERS.test(s);

// Allow: One or two words, letters only
const NAME_CHARACTERS = /^[A-Z][a-z]+(?: [A-Z][a-z]+)?$/;
export const isValidName = (s) => NAME_CHARACTERS.test(s);

const ILLEGAL_NAMES = ['admin', 'god', 'owner', 'mod', 'staff', 'server', 'gm', 'gamemaster', 'cm', 'community', 'manager', 'tutor'];
export const isIllegalname = (s) => {
    s = s.toLowerCase();
    for (const name of ILLEGAL_NAMES) {
        if (s.includes(name)) return true;
    };
    return false;
};

export const apiFetch = async (apiRoute, method, data) => {
    const requestOnce = async (csrfToken) => {
        const apiData = {
            method,
            headers: {
                "Content-Type": "application/json",
                ...(csrfToken ? {
                    "X-CSRFToken": csrfToken,
                    "X-CSRF-Token": csrfToken,
                    "CSRF-Token": csrfToken,
                    "X-XSRF-TOKEN": csrfToken,
                } : {}),
            },
            credentials: "include",
        };

        if (method !== 'GET') {
            apiData.body = JSON.stringify({ ...(data || {}), ...(csrfToken ? { csrf_token: csrfToken } : {}) });
        }

        const response = await fetch(`${apiUrl}/api${apiRoute}`, apiData);
        const json = await response.json();
        const successData = normalizeSuccessData(json);
        const failureData = normalizeApiErrors(extractFailureData(json), 'The request could not be completed.');

        return {
            code: json.code === 'success' ? 'success' : 'fail',
            data: json.code === 'success' ? successData : failureData
        };
    };

    try {
        let csrfToken = method === 'GET' ? '' : (getCsrfToken() || await refreshCsrfToken());
        let result = await requestOnce(csrfToken);

        if (method !== 'GET' && result.code !== 'success' && hasCsrfError(result.data)) {
            csrfToken = await refreshCsrfToken();
            result = await requestOnce(csrfToken);
        }

        return result;
    } catch (error) {
        return {
            code: 'fail',
            data: normalizeApiErrors(null, 'Unable to reach the server. Please try again.')
        };
    }
};